/**
 * Спарклайн строки «Голоса клиента» (#2394 §9 п. 7): доля K/N по корзинам
 * окна. Пустая корзина — разрыв линии, а не ноль; смена версии кодов — тоже
 * разрыв. Одиночная точка между разрывами — кружок. Подпись для чтения
 * экраном называет корзину и долю крайних точек.
 *
 * В узкой карточке (рамка Bitrix24) линия прячется раньше чисел строки —
 * по ширине карточки (`@container` у неё), а не окна (спека §6).
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  seriesShares,
  sparklineLabel,
  sparklineSegments,
  type VoiceRowSeries,
  type VoiceSeriesUnit,
} from './voice-series-view-model';

const WIDTH = 64;
const HEIGHT = 20;
/** Отступ, чтобы линия толщиной 1.5 у края не обрезалась. */
const PAD = 2;

interface VoiceSparklineProps {
  series: VoiceRowSeries;
  unit: VoiceSeriesUnit;
  /** Where it shows: by the row card's width unless the caller decides. */
  className?: string;
  /**
   * Inside a control whose name it would bloat (a summary card): hidden from
   * the screen reader, the tooltip stays.
   */
  decorative?: boolean;
}

export function VoiceSparkline({
  series,
  unit,
  className,
  decorative = false,
}: VoiceSparklineProps) {
  const { t } = useTranslation('dashboard');
  const shares = seriesShares(series);
  const segments = sparklineSegments(shares, series.breaks, WIDTH, HEIGHT);
  // What the line shows, in words — for a screen reader and the tooltip.
  const label = sparklineLabel(shares, unit, t);
  return (
    <svg
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
      width={WIDTH + PAD * 2}
      height={HEIGHT + PAD * 2}
      viewBox={`${-PAD} ${-PAD} ${WIDTH + PAD * 2} ${HEIGHT + PAD * 2}`}
      className={cn('hidden shrink-0 @sm:block', className)}
      data-testid="voice-sparkline"
    >
      <title>{label}</title>
      <line
        x1={0}
        y1={HEIGHT}
        x2={WIDTH}
        y2={HEIGHT}
        className="stroke-border"
        strokeWidth={1}
      />
      {segments.map((points, index) =>
        points.length === 1 ? (
          <circle
            key={index}
            cx={points[0]!.x}
            cy={points[0]!.y}
            r={1.5}
            className="fill-primary"
          />
        ) : (
          <polyline
            key={index}
            points={points.map((point) => `${point.x},${point.y}`).join(' ')}
            className="fill-none stroke-primary"
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )
      )}
    </svg>
  );
}
